import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '../config';
import LoadingSpinner from './LoadingSpinner';
import CUALaccionMenu from './CUALaccionMenu';
import ChatComposer from './ChatComposer';

const ICE_SERVERS = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }] };

const MessagingComponent = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [ws, setWs] = useState(null);
  const [isTyping, setIsTyping] = useState({});
  const messagesEndRef = useRef(null);

  // WebRTC state
  const [callState, setCallState] = useState(null); // null | 'calling' | 'ringing' | 'active'
  const [callType, setCallType] = useState(null); // 'voice' | 'video'
  const [incomingCall, setIncomingCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const peerConnection = useRef(null);
  const localStream = useRef(null);
  const remoteStream = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const callTargetUser = useRef(null);
  const callTimerRef = useRef(null);
  const wsRef = useRef(null);

  // Escrow toasts
  const [escrowToasts, setEscrowToasts] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [msgSearch, setMsgSearch] = useState('');
  const [msgSearchResults, setMsgSearchResults] = useState([]);
  const [showMsgSearch, setShowMsgSearch] = useState(false);

  // Voice notes
  const [recording, setRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordStreamRef = useRef(null);
  const recordTimerRef = useRef(null);
  const recordStartRef = useRef(0);

  // CUALaccion Hub
  const [showCualaccion, setShowCualaccion] = useState(false);
  const [showQuickTransfer, setShowQuickTransfer] = useState(null); // {currency}
  const [showQuickGiftcard, setShowQuickGiftcard] = useState(false);

  // ============ WebRTC Functions ============

  const cleanupCall = useCallback(() => {
    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }
    if (localStream.current) {
      localStream.current.getTracks().forEach(t => t.stop());
      localStream.current = null;
    }
    if (remoteStream.current) {
      remoteStream.current = null;
    }
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    setCallState(null);
    setCallType(null);
    setCallDuration(0);
    callTargetUser.current = null;
  }, []);

  const endCall = useCallback((sendSignal = true) => {
    if (sendSignal && wsRef.current?.readyState === WebSocket.OPEN && callTargetUser.current) {
      wsRef.current.send(JSON.stringify({
        type: 'call_end',
        target_user_id: callTargetUser.current
      }));
    }
    cleanupCall();
  }, [cleanupCall]);

  const createPeerConnection = useCallback((targetUserId) => {
    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          target_user_id: targetUserId,
          candidate: event.candidate.toJSON()
        }));
      }
    };

    pc.ontrack = (event) => {
      remoteStream.current = event.streams[0];
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        endCall(false);
      }
    };

    peerConnection.current = pc;
    return pc;
  }, [endCall]);

  const startCall = useCallback(async (type) => {
    if (!selectedConversation || callState) return;

    const targetParticipant = selectedConversation.participants?.[0];
    if (!targetParticipant) return;
    const targetUserId = targetParticipant.id;

    try {
      const constraints = { audio: true, video: type === 'video' };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStream.current = stream;

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      callTargetUser.current = targetUserId;
      setCallType(type);
      setCallState('calling');

      const pc = createPeerConnection(targetUserId);
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'call_offer',
          target_user_id: targetUserId,
          call_type: type,
          offer: { type: offer.type, sdp: offer.sdp },
          conversation_id: selectedConversation.id
        }));
      }
    } catch (err) {
      console.error('Error starting call:', err);
      cleanupCall();
      alert('No se pudo acceder al microfono/camara. Verifica los permisos.');
    }
  }, [selectedConversation, callState, createPeerConnection, cleanupCall]);

  const acceptCall = useCallback(async () => {
    if (!incomingCall) return;

    try {
      const constraints = { audio: true, video: incomingCall.callType === 'video' };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStream.current = stream;

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      callTargetUser.current = incomingCall.callerId;
      setCallType(incomingCall.callType);
      setCallState('active');

      const pc = createPeerConnection(incomingCall.callerId);
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      await pc.setRemoteDescription(new RTCSessionDescription(incomingCall.offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'call_answer',
          target_user_id: incomingCall.callerId,
          answer: { type: answer.type, sdp: answer.sdp },
          conversation_id: incomingCall.conversationId
        }));
      }

      // Start call timer
      setCallDuration(0);
      callTimerRef.current = setInterval(() => setCallDuration(prev => prev + 1), 1000);

      setIncomingCall(null);
    } catch (err) {
      console.error('Error accepting call:', err);
      cleanupCall();
      setIncomingCall(null);
      alert('No se pudo acceder al microfono/camara.');
    }
  }, [incomingCall, createPeerConnection, cleanupCall]);

  const rejectCall = useCallback(() => {
    if (!incomingCall) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'call_reject',
        target_user_id: incomingCall.callerId
      }));
    }
    setIncomingCall(null);
  }, [incomingCall]);

  const handleCallAnswer = useCallback(async (answer) => {
    if (!peerConnection.current) return;
    try {
      await peerConnection.current.setRemoteDescription(new RTCSessionDescription(answer));
      setCallState('active');
      setCallDuration(0);
      callTimerRef.current = setInterval(() => setCallDuration(prev => prev + 1), 1000);
    } catch (err) {
      console.error('Error handling call answer:', err);
      endCall(true);
    }
  }, [endCall]);

  const handleIceCandidate = useCallback(async (candidate) => {
    if (!peerConnection.current) return;
    try {
      await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (err) {
      console.error('Error adding ICE candidate:', err);
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (localStream.current) {
      const audioTrack = localStream.current.getAudioTracks()[0];
      if (audioTrack) audioTrack.enabled = !audioTrack.enabled;
    }
  }, []);

  const toggleVideo = useCallback(() => {
    if (localStream.current) {
      const videoTrack = localStream.current.getVideoTracks()[0];
      if (videoTrack) videoTrack.enabled = !videoTrack.enabled;
    }
  }, []);

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // ============ Voice Notes ============

  const cleanupRecording = () => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (recordStreamRef.current) {
      recordStreamRef.current.getTracks().forEach(t => t.stop());
      recordStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    recordedChunksRef.current = [];
    setRecording(false);
    setRecordingDuration(0);
  };

  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recordedChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      recordStartRef.current = Date.now();
      setRecordingDuration(0);
      recordTimerRef.current = setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - recordStartRef.current) / 1000));
      }, 500);
    } catch (err) {
      alert('No se pudo acceder al microfono. Revisa los permisos.');
      cleanupRecording();
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }
    }
    cleanupRecording();
  };

  const stopAndSendRecording = async () => {
    if (!mediaRecorderRef.current || !selectedConversation) {
      cleanupRecording();
      return;
    }
    const recorder = mediaRecorderRef.current;
    const duration = Math.floor((Date.now() - recordStartRef.current) / 1000);
    if (duration < 1) {
      cancelRecording();
      return;
    }

    const blobReady = new Promise((resolve) => {
      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: recorder.mimeType });
        resolve(blob);
      };
    });
    try { recorder.stop(); } catch (e) { /* ignore */ }
    const audioBlob = await blobReady;
    if (recordStreamRef.current) {
      recordStreamRef.current.getTracks().forEach(t => t.stop());
      recordStreamRef.current = null;
    }
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecording(false);
    setRecordingDuration(0);
    setUploadingVoice(true);

    try {
      const fd = new FormData();
      fd.append('audio', audioBlob, `voice-${Date.now()}.webm`);
      fd.append('duration_seconds', String(duration));
      if (replyTo) fd.append('reply_to', replyTo.id);

      const res = await axios.post(
        `${API}/messaging/conversations/${selectedConversation.id}/voice`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setMessages(prev => [...prev, {
        id: res.data.message_id,
        sender_id: 'me',
        is_mine: true,
        message_type: 'voice',
        content: res.data.transcript || `Nota de voz (${duration}s)`,
        metadata: {
          audio_url: res.data.audio_url,
          duration_seconds: duration,
          transcript: res.data.transcript,
          transcript_status: res.data.transcript_status,
        },
        status: 'sent',
        created_at: res.data.created_at,
      }]);
      setReplyTo(null);
      fetchConversations();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error enviando nota de voz');
    } finally {
      setUploadingVoice(false);
      cleanupRecording();
    }
  };

  // ============ CUALaccion handler ============

  const handleCualaccionAction = (actionId) => {
    if (actionId === 'transfer_cc') {
      setShowQuickTransfer({ currency: 'CUALCOIN' });
    } else if (actionId === 'transfer_pv') {
      setShowQuickTransfer({ currency: 'PUNTOS_VERDES' });
    } else if (actionId === 'send_giftcard') {
      // Redirigir al modulo de CUALgiftcard
      window.dispatchEvent(new CustomEvent('cualapp:navigate', { detail: { module: 'cualgiftcard' } }));
    } else if (actionId === 'create_escrow') {
      window.dispatchEvent(new CustomEvent('cualapp:navigate', { detail: { module: 'escrow' } }));
    } else if (actionId === 'share_invite') {
      window.dispatchEvent(new CustomEvent('cualapp:navigate', { detail: { module: 'cualshare' } }));
    } else if (actionId === 'split_bill') {
      window.dispatchEvent(new CustomEvent('cualapp:navigate', { detail: { module: 'cualshare' } }));
    } else if (actionId === 'request_transport') {
      window.dispatchEvent(new CustomEvent('cualapp:navigate', { detail: { module: 'transport' } }));
    } else if (actionId === 'view_marketplace') {
      window.dispatchEvent(new CustomEvent('cualapp:navigate', { detail: { module: 'marketplace' } }));
    }
  };

  const submitQuickTransfer = async (amount, concept) => {
    if (!selectedConversation || !showQuickTransfer) return;
    const targetUser = selectedConversation.participants?.[0];
    if (!targetUser) return;
    try {
      await axios.post(`${API}/wallet/send`, {
        recipient_user_id: targetUser.id,
        amount: parseFloat(amount),
        currency: showQuickTransfer.currency,
        description: concept || 'Transferencia desde chat',
      });
      // Post system message
      await axios.post(
        `${API}/messaging/conversations/${selectedConversation.id}/messages`,
        {
          content: `Transferencia: ${amount} ${showQuickTransfer.currency === 'CUALCOIN' ? 'Cc' : 'CV'}${concept ? ' - ' + concept : ''}`,
          message_type: 'system',
        }
      );
      setShowQuickTransfer(null);
      fetchMessages(selectedConversation.id);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error en transferencia');
    }
  };

  // ============ WebSocket Connection ============

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${new URL(BACKEND_URL).host}/ws/chat/${token}`;
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('WebSocket connected');
      setWs(websocket);
      wsRef.current = websocket;
    };

    websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'new_message') {
          setMessages(prev => {
            if (prev.length > 0 && prev[prev.length - 1]?.id === data.message?.id) return prev;
            return [...prev, { ...data.message, is_mine: false }];
          });
          fetchConversations();
        }

        if (data.type === 'user_typing') {
          setIsTyping(prev => ({ ...prev, [data.conversation_id]: data.user_id }));
          setTimeout(() => {
            setIsTyping(prev => {
              const ns = { ...prev };
              delete ns[data.conversation_id];
              return ns;
            });
          }, 3000);
        }

        if (data.type === 'messages_read') {
          // Update all my messages to 'read' status
          setMessages(prev => prev.map(m =>
            m.is_mine ? { ...m, status: 'read' } : m
          ));
        }

        if (data.type === 'escrow_notification') {
          handleEscrowNotification(data);
        }

        // WebRTC signaling
        if (data.type === 'call_offer') {
          setIncomingCall({
            callerId: data.caller_id,
            callerName: data.caller_name,
            callType: data.call_type,
            offer: data.offer,
            conversationId: data.conversation_id
          });
        }
        if (data.type === 'call_answer') {
          handleCallAnswer(data.answer);
        }
        if (data.type === 'ice_candidate') {
          handleIceCandidate(data.candidate);
        }
        if (data.type === 'call_end' || data.type === 'call_reject') {
          endCall(false);
          setIncomingCall(null);
        }
        if (data.type === 'call_error') {
          alert(data.error);
          endCall(false);
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };

    websocket.onerror = (error) => console.log('WebSocket error:', error);
    websocket.onclose = () => {
      console.log('WebSocket disconnected');
      setWs(null);
      wsRef.current = null;
    };

    return () => {
      if (websocket.readyState === WebSocket.OPEN) websocket.close();
    };
  }, []); // WS connection only on mount

  // ============ Escrow Notifications ============

  const handleEscrowNotification = (data) => {
    const { notification_type, escrow, other_user_email } = data;
    let title = '', message = '', icon = '';
    switch (notification_type) {
      case 'escrow_created':
        title = 'Nuevo Escrow Recibido';
        message = `${other_user_email} retuvo ${escrow.amount} ${escrow.currency} para ti`;
        icon = 'lock'; break;
      case 'escrow_released':
        title = 'Fondos Liberados';
        message = `${other_user_email} libero ${escrow.amount} ${escrow.currency}. Ya estan en tu wallet!`;
        icon = 'check'; break;
      case 'escrow_refunded':
        title = 'Escrow Devuelto';
        message = `${other_user_email} devolvio ${escrow.amount} ${escrow.currency}`;
        icon = 'undo'; break;
      default: return;
    }
    showEscrowToast(title, message, icon, notification_type);
  };

  const showEscrowToast = (title, message, icon, type) => {
    const id = Date.now();
    setEscrowToasts(prev => [...prev, { id, title, message, icon, type }]);
    setTimeout(() => setEscrowToasts(prev => prev.filter(t => t.id !== id)), 8000);
  };

  const removeToast = (id) => setEscrowToasts(prev => prev.filter(t => t.id !== id));

  // ============ Chat Functions ============

  useEffect(() => { fetchConversations(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messaging/conversations`);
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/messaging/conversations/${conversationId}/messages`);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    setReplyTo(null);
    setShowMsgSearch(false);
    setMsgSearch('');
    setMsgSearchResults([]);
    fetchMessages(conv.id);
    // Send read receipt via WebSocket
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'mark_read', conversation_id: conv.id }));
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    setSending(true);
    try {
      const payload = { content: newMessage, message_type: 'text' };
      if (replyTo) payload.reply_to = replyTo.id;
      const response = await axios.post(
        `${API}/messaging/conversations/${selectedConversation.id}/messages`,
        payload
      );
      setMessages(prev => [...prev, {
        id: response.data.message_id,
        content: newMessage,
        is_mine: true,
        status: 'sent',
        reply_to: replyTo?.id || null,
        reply_preview: replyTo ? { sender_name: replyTo.sender_name || 'Tu', content: replyTo.content?.substring(0, 80) } : null,
        created_at: response.data.created_at
      }]);
      setNewMessage('');
      setReplyTo(null);
      fetchConversations();
    } catch (error) {
      alert('Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  const searchUsers = async (query) => {
    if (query.length < 2) { setSearchResults([]); return; }
    try {
      const response = await axios.get(`${API}/messaging/users/search?query=${query}`);
      setSearchResults(response.data.users);
    } catch (error) {
      console.error('Failed to search users:', error);
    }
  };

  const startDirectChat = async (userId) => {
    try {
      const response = await axios.post(`${API}/messaging/conversations/direct`, { recipient_user_id: userId });
      fetchConversations();
      setShowNewChat(false);
      setSearchQuery('');
      setSearchResults([]);
      const convId = response.data.conversation_id;
      const conv = conversations.find(c => c.id === convId) || { id: convId };
      selectConversation(conv);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear chat');
    }
  };

  const createGroup = async () => {
    if (!groupName.trim() || selectedUsers.length === 0) {
      alert('Ingresa nombre del grupo y selecciona participantes');
      return;
    }
    try {
      await axios.post(`${API}/messaging/conversations/group`, {
        name: groupName,
        participant_ids: selectedUsers.map(u => u.id)
      });
      fetchConversations();
      setShowNewGroup(false);
      setGroupName('');
      setSelectedUsers([]);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear grupo');
    }
  };

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && selectedConversation) {
      wsRef.current.send(JSON.stringify({ type: 'typing', conversation_id: selectedConversation.id }));
    }
  };

  const searchMessages = async (query) => {
    setMsgSearch(query);
    if (query.length < 2 || !selectedConversation) { setMsgSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/messaging/conversations/${selectedConversation.id}/search?q=${encodeURIComponent(query)}`);
      setMsgSearchResults(res.data.results || []);
    } catch (err) { setMsgSearchResults([]); }
  };

  const getConversationName = (conv) => {
    if (conv.name) return conv.name;
    if (conv.participants?.length > 0) return conv.participants.map(p => p.name).join(', ');
    return 'Chat';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex h-[calc(100vh-200px)] bg-white rounded-xl shadow-lg overflow-hidden relative">
      {/* ===== Conversations List ===== */}
      <div className="w-1/3 border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <h2 className="text-lg font-bold text-gray-900" data-testid="messaging-title">CM / CC / CV</h2>
          <p className="text-xs text-gray-500 mt-0.5">Mensaje - Llamada - Video</p>
          <div className="flex space-x-2 mt-2">
            <button onClick={() => setShowNewChat(true)} data-testid="new-chat-btn"
              className="flex-1 text-xs bg-purple-600 text-white py-2 px-3 rounded-lg hover:bg-purple-700 transition-colors">
              + Nuevo Chat
            </button>
            <button onClick={() => setShowNewGroup(true)} data-testid="new-group-btn"
              className="flex-1 text-xs bg-blue-600 text-white py-2 px-3 rounded-lg hover:bg-blue-700 transition-colors">
              + Nuevo Grupo
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <p className="text-sm">No tienes conversaciones</p>
              <p className="text-xs mt-1">Inicia un chat nuevo</p>
            </div>
          ) : conversations.map((conv) => (
            <div key={conv.id} onClick={() => selectConversation(conv)} data-testid={`conversation-${conv.id}`}
              className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                selectedConversation?.id === conv.id ? 'bg-purple-50 border-l-4 border-l-purple-500' : ''
              }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                    conv.type === 'group' ? 'bg-blue-500' : conv.type === 'transaction' ? 'bg-green-500' : 'bg-purple-500'
                  }`}>
                    {conv.type === 'group' ? 'G' : conv.type === 'transaction' ? 'T' : (getConversationName(conv)?.[0] || 'C')}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{getConversationName(conv)}</p>
                    <p className="text-xs text-gray-500 truncate max-w-[150px]">{conv.last_message || 'Sin mensajes'}</p>
                  </div>
                </div>
                {conv.unread_count > 0 && (
                  <span className="bg-purple-600 text-white text-xs rounded-full px-2 py-1">{conv.unread_count}</span>
                )}
              </div>
              {isTyping[conv.id] && <p className="text-xs text-purple-600 mt-1 animate-pulse">Escribiendo...</p>}
            </div>
          ))}
        </div>
      </div>

      {/* ===== Chat Area ===== */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header with CC/CV buttons */}
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                    selectedConversation.type === 'group' ? 'bg-blue-500' : 'bg-purple-500'
                  }`}>
                    {selectedConversation.type === 'group' ? 'G' : (getConversationName(selectedConversation)?.[0] || 'C')}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{getConversationName(selectedConversation)}</p>
                    <p className="text-xs text-gray-500">
                      {selectedConversation.participants?.filter(p => p.is_online).length || 0} en linea
                    </p>
                  </div>
                </div>
                {selectedConversation.type === 'direct' && !callState && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => setShowMsgSearch(!showMsgSearch)} data-testid="msg-search-btn" title="Buscar en chat"
                      className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center transition-colors">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                      </svg>
                    </button>
                    <button onClick={() => startCall('voice')} data-testid="cc-call-btn" title="CC - CUAL Llamada"
                      className="w-10 h-10 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-colors shadow-sm">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                      </svg>
                    </button>
                    <button onClick={() => startCall('video')} data-testid="cv-video-btn" title="CV - CUAL Video"
                      className="w-10 h-10 rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center transition-colors shadow-sm">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Message Search Bar */}
            {showMsgSearch && (
              <div className="px-4 py-2 border-b border-gray-200 bg-white" data-testid="msg-search-bar">
                <input type="text" value={msgSearch} onChange={e => searchMessages(e.target.value)}
                  placeholder="Buscar en esta conversacion..." autoFocus data-testid="msg-search-input"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-300 focus:border-purple-400" />
                {msgSearchResults.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                    {msgSearchResults.map(r => (
                      <div key={r.id} className="p-2 rounded-lg bg-gray-50 hover:bg-purple-50 cursor-pointer text-xs" data-testid={`search-result-${r.id}`}>
                        <span className="font-medium text-purple-600">{r.sender_name}</span>
                        <span className="text-gray-500 ml-1">{r.content?.substring(0, 60)}{r.content?.length > 60 ? '...' : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((msg, idx) => (
                <div key={msg.id || idx} className={`flex ${msg.is_mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] group relative ${
                    msg.message_type === 'system' ? '' : ''
                  }`}>
                    {/* Reply preview */}
                    {msg.reply_preview && (
                      <div className={`text-[10px] px-3 py-1 rounded-t-xl border-l-2 ${
                        msg.is_mine ? 'bg-purple-500/20 border-purple-300 text-purple-200' : 'bg-gray-100 border-purple-400 text-gray-500'
                      }`}>
                        <span className="font-medium">{msg.reply_preview.sender_name}</span>
                        <p className="truncate">{msg.reply_preview.content}</p>
                      </div>
                    )}
                    <div className={`rounded-2xl px-4 py-2 ${
                      msg.reply_preview ? 'rounded-t-none' : ''
                    } ${
                      msg.message_type === 'system'
                        ? 'bg-gray-200 text-gray-600 text-center text-xs mx-auto'
                        : msg.is_mine
                          ? 'bg-purple-600 text-white rounded-br-sm'
                          : 'bg-white text-gray-900 shadow-sm rounded-bl-sm'
                    }`}>
                      {!msg.is_mine && msg.message_type !== 'system' && (
                        <p className="text-xs text-purple-600 font-medium mb-1">{msg.sender_name}</p>
                      )}
                      {/* Sticker rendering: sin burbuja */}
                      {msg.metadata?.is_sticker || msg.metadata?.sticker ? (
                        <div data-testid={`sticker-msg-${msg.id}`} className="flex flex-col items-center">
                          {msg.metadata.sticker.official ? (
                            <div
                              className="w-32 h-32 rounded-2xl flex flex-col items-center justify-center shadow-md"
                              style={{
                                background: `linear-gradient(135deg, ${msg.metadata.sticker.color}33 0%, ${msg.metadata.sticker.color}88 100%)`,
                                border: `3px solid ${msg.metadata.sticker.color}cc`,
                              }}
                            >
                              <span className="text-6xl">{msg.metadata.sticker.emoji}</span>
                              <span className="text-xs mt-1 font-semibold text-gray-700">{msg.metadata.sticker.label}</span>
                            </div>
                          ) : (
                            <img
                              src={`${BACKEND_URL}${msg.metadata.sticker.url}`}
                              alt={msg.metadata.sticker.label}
                              className="w-32 h-32 object-contain"
                            />
                          )}
                          <span className="text-[10px] text-gray-400 mt-0.5">por {msg.metadata.sticker.author_name}</span>
                        </div>
                      ) : msg.message_type === 'image' && msg.metadata?.url ? (
                        <div className="space-y-1 max-w-xs">
                          <img
                            src={`${BACKEND_URL}${msg.metadata.url}`}
                            alt={msg.metadata.filename || 'imagen'}
                            data-testid={`image-msg-${msg.id}`}
                            className="rounded-lg max-w-full max-h-64 object-cover cursor-pointer"
                            onClick={() => window.open(`${BACKEND_URL}${msg.metadata.url}`, '_blank')}
                          />
                          {msg.content && msg.content !== msg.metadata.filename && (
                            <p className="text-sm">{msg.content}</p>
                          )}
                        </div>
                      ) : msg.message_type === 'file' && msg.metadata?.url ? (
                        <a
                          href={`${BACKEND_URL}${msg.metadata.url}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`file-msg-${msg.id}`}
                          className={`flex items-center gap-2 p-2 rounded-lg ${msg.is_mine ? 'bg-purple-700 hover:bg-purple-800' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                        >
                          <svg className={`w-8 h-8 flex-shrink-0 ${msg.is_mine ? 'text-purple-200' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div className="min-w-0">
                            <p className={`text-sm font-medium truncate ${msg.is_mine ? 'text-white' : 'text-gray-800'}`}>{msg.metadata.filename}</p>
                            <p className={`text-xs ${msg.is_mine ? 'text-purple-200' : 'text-gray-500'}`}>
                              {((msg.metadata.size_bytes || 0) / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </a>
                      ) : msg.message_type === 'voice' && msg.metadata?.audio_url ? (
                        <div className="space-y-2 min-w-[220px]">
                          <audio
                            controls
                            src={`${BACKEND_URL}${msg.metadata.audio_url}`}
                            data-testid={`voice-player-${msg.id}`}
                            className="w-full h-9"
                          />
                          {msg.metadata.transcript ? (
                            <p className={`text-xs italic ${msg.is_mine ? 'text-purple-100' : 'text-gray-600'}`} data-testid={`voice-transcript-${msg.id}`}>
                              "{msg.metadata.transcript}"
                            </p>
                          ) : msg.metadata.transcript_status === 'failed' ? (
                            <p className={`text-xs ${msg.is_mine ? 'text-purple-200' : 'text-gray-400'}`}>(sin transcripcion)</p>
                          ) : null}
                        </div>
                      ) : msg.message_type === 'action' ? (
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{msg.content}</p>
                          {msg.metadata?.action_type === 'share_location' && msg.metadata?.lat && (
                            <a
                              href={`https://maps.google.com/?q=${msg.metadata.lat},${msg.metadata.lng}`}
                              target="_blank" rel="noreferrer"
                              data-testid={`location-link-${msg.id}`}
                              className={`inline-block text-xs underline ${msg.is_mine ? 'text-purple-100' : 'text-purple-600'}`}
                            >
                              Abrir en Google Maps
                            </a>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm">{msg.content}</p>
                      )}
                      <div className={`flex items-center gap-1 mt-1 ${msg.is_mine ? 'justify-end' : ''}`}>
                        <p className={`text-xs ${msg.is_mine ? 'text-purple-200' : 'text-gray-400'}`}>
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                        {msg.is_mine && msg.message_type !== 'system' && (
                          <span className={`text-[10px] ${msg.status === 'read' ? 'text-blue-300' : 'text-purple-300'}`} data-testid="msg-status">
                            {msg.status === 'read' ? '✓✓' : msg.status === 'delivered' ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Reply button (hover) */}
                    {msg.message_type !== 'system' && (
                      <button onClick={() => setReplyTo(msg)} data-testid={`reply-btn-${msg.id || idx}`}
                        className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center ${
                          msg.is_mine ? '-left-8' : '-right-8'
                        }`}>
                        <svg className="w-3 h-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="border-t border-gray-200 bg-white">
              {/* Reply preview bar */}
              {replyTo && (
                <div className="px-4 py-2 bg-purple-50 border-b border-purple-200 flex items-center justify-between" data-testid="reply-preview">
                  <div className="flex items-center gap-2 text-xs text-purple-700 min-w-0">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                    </svg>
                    <span className="font-medium">{replyTo.is_mine ? 'Tu' : replyTo.sender_name}</span>
                    <span className="text-gray-500 truncate">{replyTo.content?.substring(0, 50)}</span>
                  </div>
                  <button type="button" onClick={() => setReplyTo(null)} data-testid="cancel-reply"
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              )}

              <ChatComposer
                showCualaccion={selectedConversation.type === 'direct'}
                onOpenCualaccion={() => setShowCualaccion(true)}
                onSendText={async (text) => {
                  await axios.post(
                    `${API}/messaging/conversations/${selectedConversation.id}/messages`,
                    {
                      content: text,
                      message_type: 'text',
                      reply_to: replyTo?.id || null,
                    }
                  );
                  setReplyTo(null);
                  fetchMessages(selectedConversation.id);
                }}
                onSendVoice={async ({ audioBlob, duration }) => {
                  const fd = new FormData();
                  fd.append('audio', audioBlob, `voice-${Date.now()}.webm`);
                  fd.append('duration_seconds', String(duration));
                  if (replyTo) fd.append('reply_to', replyTo.id);
                  await axios.post(
                    `${API}/messaging/conversations/${selectedConversation.id}/voice`,
                    fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                  );
                  setReplyTo(null);
                  fetchMessages(selectedConversation.id);
                }}
                onSendAttachment={async (file, caption) => {
                  const fd = new FormData();
                  fd.append('file', file);
                  if (caption) fd.append('caption', caption);
                  await axios.post(
                    `${API}/messaging/conversations/${selectedConversation.id}/attachment`,
                    fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                  );
                  fetchMessages(selectedConversation.id);
                }}
                onSendSticker={async (sticker_id) => {
                  await axios.post(`${API}/stickers/send`, {
                    conversation_id: selectedConversation.id,
                    sticker_id,
                  });
                  fetchMessages(selectedConversation.id);
                }}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center text-gray-500">
              <svg className="w-16 h-16 mx-auto text-purple-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
              <p className="mt-4 text-lg font-medium">Selecciona una conversacion</p>
              <p className="text-sm">o inicia un nuevo chat</p>
            </div>
          </div>
        )}
      </div>

      {/* ===== Active Call Overlay ===== */}
      {callState && (
        <div className="absolute inset-0 z-40 bg-gray-900/95 flex flex-col items-center justify-center" data-testid="call-overlay">
          {/* Call info */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-purple-600 flex items-center justify-center text-white text-3xl font-bold mx-auto mb-4">
              {callType === 'video' ? (
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
              ) : (
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
              )}
            </div>
            <h3 className="text-white text-xl font-semibold" data-testid="call-status-label">
              {callState === 'calling' ? 'Llamando...' : callState === 'ringing' ? 'Sonando...' : `${callType === 'video' ? 'CV' : 'CC'} - Llamada Activa`}
            </h3>
            {callState === 'active' && (
              <p className="text-purple-300 text-lg mt-1 font-mono" data-testid="call-duration">{formatDuration(callDuration)}</p>
            )}
            {callState === 'calling' && (
              <div className="mt-2 flex items-center justify-center space-x-1">
                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}}></span>
                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}}></span>
                <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}}></span>
              </div>
            )}
          </div>

          {/* Video feeds */}
          {callType === 'video' && (
            <div className="relative w-full max-w-2xl aspect-video mb-8 bg-black rounded-xl overflow-hidden">
              <video ref={remoteVideoRef} autoPlay playsInline data-testid="remote-video"
                className="w-full h-full object-cover" />
              <video ref={localVideoRef} autoPlay playsInline muted data-testid="local-video"
                className="absolute bottom-3 right-3 w-32 h-24 rounded-lg border-2 border-white/30 object-cover bg-gray-800" />
            </div>
          )}

          {/* Audio-only local preview (hidden) */}
          {callType === 'voice' && (
            <>
              <audio ref={remoteVideoRef} autoPlay />
              <audio ref={localVideoRef} autoPlay muted />
            </>
          )}

          {/* Call controls */}
          <div className="flex items-center gap-4">
            <button onClick={toggleMute} data-testid="call-mute-btn" title="Silenciar"
              className="w-14 h-14 rounded-full bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center transition-colors">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
              </svg>
            </button>

            {callType === 'video' && (
              <button onClick={toggleVideo} data-testid="call-toggle-video-btn" title="Camara"
                className="w-14 h-14 rounded-full bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center transition-colors">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
              </button>
            )}

            <button onClick={() => endCall(true)} data-testid="call-end-btn" title="Colgar"
              className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors shadow-lg">
              <svg className="w-7 h-7 transform rotate-[135deg]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
              </svg>
            </button>
          </div>

          {/* Data consumption warning */}
          {callType === 'video' && (
            <p className="mt-6 text-xs text-gray-400" data-testid="data-warning">
              CV consume mas datos. Usa CC (voz) para ahorrar datos moviles.
            </p>
          )}
        </div>
      )}

      {/* ===== Incoming Call Modal ===== */}
      {incomingCall && !callState && (
        <div className="absolute inset-0 z-50 bg-black/80 flex items-center justify-center" data-testid="incoming-call-modal">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
            <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-white ${
              incomingCall.callType === 'video' ? 'bg-blue-500' : 'bg-green-500'
            }`}>
              {incomingCall.callType === 'video' ? (
                <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
              ) : (
                <svg className="w-10 h-10 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
              )}
            </div>

            <h3 className="text-xl font-bold text-gray-900" data-testid="incoming-caller-name">{incomingCall.callerName}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {incomingCall.callType === 'video' ? 'CV - Video llamada entrante' : 'CC - Llamada de voz entrante'}
            </p>

            <div className="flex items-center justify-center gap-6 mt-8">
              <button onClick={rejectCall} data-testid="reject-call-btn"
                className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors shadow-lg">
                <svg className="w-7 h-7 transform rotate-[135deg]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
              </button>
              <button onClick={acceptCall} data-testid="accept-call-btn"
                className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-colors shadow-lg animate-pulse">
                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== New Chat Modal ===== */}
      {showNewChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Nuevo Chat</h3>
            <input type="text" value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); searchUsers(e.target.value); }}
              placeholder="Buscar usuario por nombre o email..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" />
            <div className="mt-4 max-h-60 overflow-y-auto">
              {searchResults.map((user) => (
                <div key={user.id} onClick={() => startDirectChat(user.id)}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-sm font-bold text-purple-700">
                      {user.full_name?.[0] || 'U'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user.full_name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <span className={`w-3 h-3 rounded-full ${user.is_online ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                </div>
              ))}
            </div>
            <button onClick={() => { setShowNewChat(false); setSearchQuery(''); setSearchResults([]); }}
              className="mt-4 w-full py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ===== New Group Modal ===== */}
      {showNewGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Nuevo Grupo</h3>
            <input type="text" value={groupName} onChange={(e) => setGroupName(e.target.value)}
              placeholder="Nombre del grupo"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4" />
            <input type="text" value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); searchUsers(e.target.value); }}
              placeholder="Buscar usuarios para agregar..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" />
            {selectedUsers.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedUsers.map((user) => (
                  <span key={user.id} className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs flex items-center">
                    {user.full_name}
                    <button onClick={() => setSelectedUsers(prev => prev.filter(u => u.id !== user.id))} className="ml-1 text-purple-500 hover:text-purple-700">x</button>
                  </span>
                ))}
              </div>
            )}
            <div className="mt-4 max-h-40 overflow-y-auto">
              {searchResults.filter(u => !selectedUsers.find(s => s.id === u.id)).map((user) => (
                <div key={user.id} onClick={() => setSelectedUsers(prev => [...prev, user])}
                  className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-sm font-bold text-purple-700">
                      {user.full_name?.[0] || 'U'}
                    </div>
                    <span className="text-sm">{user.full_name}</span>
                  </div>
                  <span className="text-purple-600">+</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex space-x-2">
              <button onClick={() => { setShowNewGroup(false); setGroupName(''); setSelectedUsers([]); setSearchQuery(''); setSearchResults([]); }}
                className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={createGroup} className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                Crear Grupo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Escrow Toasts ===== */}
      <div className="fixed top-4 right-4 z-50 space-y-2" data-testid="escrow-toasts">
        {escrowToasts.map((toast) => (
          <div key={toast.id} data-testid={`escrow-toast-${toast.type}`}
            className={`max-w-sm p-4 rounded-xl shadow-2xl border-l-4 backdrop-blur-sm transform transition-all duration-300 ease-out ${
              toast.type === 'escrow_created' ? 'bg-blue-50 border-purple-500'
              : toast.type === 'escrow_released' ? 'bg-green-50 border-green-500'
              : 'bg-orange-50 border-orange-500'
            }`}>
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-bold text-gray-900 text-sm">{toast.title}</h4>
                <p className="text-xs text-gray-600 mt-1">{toast.message}</p>
              </div>
              <button onClick={() => removeToast(toast.id)} className="text-gray-400 hover:text-gray-600 ml-2">x</button>
            </div>
          </div>
        ))}
      </div>

      {/* ===== CUALaccion Hub ===== */}
      <CUALaccionMenu
        open={showCualaccion}
        onClose={() => setShowCualaccion(false)}
        recipientUserId={selectedConversation?.participants?.[0]?.id}
        conversationId={selectedConversation?.id}
        onAction={handleCualaccionAction}
        onStartCall={(type) => startCall(type)}
      />

      {/* ===== Quick Transfer Modal ===== */}
      {showQuickTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="quick-transfer-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              Transferir {showQuickTransfer.currency === 'CUALCOIN' ? 'CUALCOIN' : 'CUALverde'}
            </h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const amt = e.target.amount.value;
              const concept = e.target.concept.value;
              submitQuickTransfer(amt, concept);
            }} className="space-y-3">
              <input
                type="number"
                step="0.01"
                name="amount"
                placeholder="Monto"
                required
                data-testid="quick-transfer-amount"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <input
                type="text"
                name="concept"
                placeholder="Concepto (opcional)"
                data-testid="quick-transfer-concept"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowQuickTransfer(null)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="submit" data-testid="quick-transfer-submit"
                  className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                  Transferir
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagingComponent;
